// Tasks and follow-ups (BL-004), the words and groups the office reads them in: by when they're due
// (overdue first), each with its due day in words, its type, and what it's about.

import { addDays, dateText, daysBetween, sentences } from '@flowology/kernel';
import { agentName } from '../books/model.ts';

export type TaskStatus = 'open' | 'in_progress' | 'waiting' | 'done' | 'cancelled';

export interface Task {
  id: string;
  rowVersion: number;
  type: { code: string; name: string };
  title: string;
  details: string | null;
  status: TaskStatus;
  priority: 'low' | 'normal' | 'high';
  source: 'office' | 'call' | 'agent' | 'review' | 'system' | 'import';
  dueOn: string | null;
  overdue: boolean;
  assignee: { id: string; name: string } | null;
  customer: { id: string; name: string } | null;
  job: { id: string; number: string } | null;
  estimateId: string | null;
  invoiceId: string | null;
  callId: string | null;
  createdAt: string;
  createdBy: string | null;
  closedAt: string | null;
  closedBy: string | null;
  closeNote: string | null;
}

export interface TaskType {
  code: string;
  name: string;
  active: boolean;
}

/**
 * Who sees and keeps tasks here: tasks.view, in the office (customers.search, as Nav's Tasks place),
 * not a technician (phase 2) or the warehouse alone. By permission, never a role name (QA-681).
 */
export const seesTasks = (held: readonly string[]): boolean =>
  held.includes('tasks.view') && held.includes('customers.search');

export const STATUS_LABEL: Record<TaskStatus, string> = {
  open: 'Open',
  in_progress: 'In progress',
  waiting: 'Waiting',
  done: 'Done',
  cancelled: 'Cancelled',
};

export const isOpen = (t: Pick<Task, 'status'>): boolean =>
  t.status === 'open' || t.status === 'in_progress' || t.status === 'waiting';

/** "Due today", "Due tomorrow", "Due Fri, Jul 17", "Overdue: due Mon, Jul 13"; null with no day. */
export function dueWords(t: Pick<Task, 'dueOn' | 'status'>, today: string, timeZone: string): string | null {
  if (!t.dueOn) return null;
  const day = dateText(t.dueOn, timeZone, { year: false, weekday: 'short' });
  const n = daysBetween(today, t.dueOn);
  if (isOpen(t) && n < 0) return `Overdue: due ${day}`;
  if (n === 0) return 'Due today';
  if (n === 1) return 'Due tomorrow';
  return `Due ${day}`;
}

export type DueGroup = 'Overdue' | 'Today' | 'Tomorrow' | 'This week' | 'Later' | 'No due day';
const GROUPS: readonly DueGroup[] = ['Overdue', 'Today', 'Tomorrow', 'This week', 'Later', 'No due day'];

/** Which group an open task sits in: the week runs to Sunday (Monday's week, as the board's). */
export function dueGroup(t: Pick<Task, 'dueOn'>, today: string): DueGroup {
  if (!t.dueOn) return 'No due day';
  const n = daysBetween(today, t.dueOn);
  if (n < 0) return 'Overdue';
  if (n === 0) return 'Today';
  if (n === 1) return 'Tomorrow';
  const weekday = new Date(`${today}T12:00:00Z`).getUTCDay() || 7; // 1 Monday … 7 Sunday
  return t.dueOn <= addDays(today, 7 - weekday) ? 'This week' : 'Later';
}

/** Open tasks by group, in the server's order within each; groups with none are left out. */
export function grouped<T extends Pick<Task, 'dueOn'>>(
  tasks: readonly T[],
  today: string,
): { group: DueGroup; tasks: T[] }[] {
  return GROUPS.map((group) => ({ group, tasks: tasks.filter((t) => dueGroup(t, today) === group) })).filter(
    (g) => g.tasks.length,
  );
}

/** A task's line under its title: its type, who has it, and how it came in. */
export function taskMeta(t: Task): string {
  return [
    t.type.name,
    t.priority === 'high' ? 'High priority' : t.priority === 'low' ? 'Low priority' : null,
    t.assignee ? t.assignee.name : 'Nobody yet',
    t.status === 'in_progress' || t.status === 'waiting' ? STATUS_LABEL[t.status] : null,
    t.source === 'agent' && t.createdBy ? `Added by ${t.createdBy}` : null,
    // brought across from ServiceTitan at cutover
    t.source === 'import' ? 'From ServiceTitan' : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

/** A closed task's line: "Done by Dana Lee: Arrived; booked for Monday." */
export function closedText(t: Task): string {
  const how = t.status === 'cancelled' ? 'Cancelled' : 'Done';
  return sentences(`${how}${t.closedBy ? ` by ${t.closedBy}` : ''}${t.closeNote ? `: ${t.closeNote}` : ''}`);
}

/** How many tasks a list reads at a time; the rest come on "Load more" (list caps, BL-110). */
export const TASK_PAGE = 100;

/** tasks.list's input for a list: its open ones unless it says, a page at a time. */
export const tasksInput = (query: Record<string, unknown>): Record<string, unknown> => ({
  state: 'open',
  ...query,
  limit: TASK_PAGE,
});

/** tasks.list's answer, a page at a time: `total` counts every task the list takes. */
export interface TaskPage {
  tasks: Task[];
  more: boolean;
  next: string | null;
  total: number;
}

/** How many of mine are due today or before: the Inbox's count on Tasks. */
export function dueCount(tasks: readonly Pick<Task, 'dueOn' | 'status'>[], today: string): number {
  return tasks.filter((t) => isOpen(t) && !!t.dueOn && t.dueOn <= today).length;
}

export interface TaskDraft {
  typeCode: string;
  title: string;
  details: string;
  dueOn: string;
  priority: 'low' | 'normal' | 'high';
  assigneeStaffId: string;
}

/** The form's draft as tasks.create's input, about what the form was opened on. */
export function createInput(
  id: string,
  d: TaskDraft,
  about: { customerId?: string; jobId?: string },
): Record<string, unknown> {
  return {
    id,
    typeCode: d.typeCode,
    title: d.title.trim(),
    ...(d.details.trim() ? { details: d.details.trim() } : {}),
    ...(d.dueOn ? { dueOn: d.dueOn } : {}),
    priority: d.priority,
    ...(d.assigneeStaffId ? { assigneeStaffId: d.assigneeStaffId } : {}),
    ...(about.jobId ? { jobId: about.jobId } : about.customerId ? { customerId: about.customerId } : {}),
  };
}

/** Why the form can't be saved yet, or null. */
export function draftProblem(d: TaskDraft): string | null {
  if (!d.typeCode) return 'Pick what kind of task it is.';
  if (!d.title.trim()) return 'Say what needs doing.';
  return null;
}

/** My work's agent request (approvals.list's row, as much as it shows). */
export interface WaitingApproval {
  id: string;
  agent: string;
  actionTitle: string;
  explanation: string | null;
  cannotDecide: string | null;
}

/** "Office copilot asks to book a visit": who asks (its name, as Agents › Approvals says it), and for what. */
export function approvalLine(a: Pick<WaitingApproval, 'agent' | 'actionTitle'>): string {
  return `${agentName(a.agent)} asks to ${a.actionTitle.charAt(0).toLowerCase()}${a.actionTitle.slice(1)}`;
}
